import _ from 'lodash';
import { metricNames } from '@linearb/linjs-common';
import TeamNameCell from './cell-components/TeamNameCell';
import MembersCell from './cell-components/MembersCell';
import CycleTimeCell from './cell-components/CycleTimeCell';
import { TABLE_MODEL } from '../../groupMetricsTable.model';
import {
  dashboardMetrics,
  getFormattedAndCalculatedV3Data,
  getMappedData
} from '../../../groupDashboard.service';
import TableCell from './cell-components/TableCell';
import {
  getTrendDirectionAndPercentChangePerMetric,
  getIconName
} from '../../GroupMetricsTable.service';
import { METRICS_COLOR_PER_DIRECTION } from '../../GroupMetricsTable.consts';
import { ROW_TYPES } from 'components/base/Table/TableConsts';
import { TEAM_TYPES } from 'common/constants/constants';

export const getTeamsTableRows = ({
  teams,
  metrics,
  availableMetricsWidgets,
  tableMetricsNames,
  metricsPerGranularity,
  aggregatedLastPeriodMetrics,
  metricsPerGranularityLastPeriod,
  lastPeriodDates,
  contributorsById = {},
  onLoadMoreClick,
  onExpandClick,
  groupsManagedPages,
  isRowLoading,
  expandedTeamId
}) => {
  const metricsWidgetsByName = _.keyBy(
    availableMetricsWidgets,
    'metrics[0].name'
  );

  return teams.map(team => {
    const { id, type } = team;
    const teamHasNoData =
      metrics.find(({ group }) => group === id) === undefined;
    const teamChildren = _.get(team, 'children', []);
    const isGroup = teamChildren.length > 0 || type === TEAM_TYPES.GROUP;
    const contributors = _.get(team, 'contributors', []);
    const { mappedV3Data, metricsPerGranularityMapped } = getMappedData(
      metrics,
      metricsPerGranularity,
      id
    );

    const {
      mappedV3Data: mappedV3DataLastPeriod,
      metricsPerGranularityMapped: metricsPerGranularityMappedLastPeriod
    } = getMappedData(
      aggregatedLastPeriodMetrics,
      metricsPerGranularityLastPeriod,
      id
    );

    const { formattedV3Data, V3DataCalculated: DataCurrentPriod } =
      getFormattedAndCalculatedV3Data(
        mappedV3Data,
        tableMetricsNames,
        availableMetricsWidgets,
        metricsPerGranularityMapped,
        2
      );

    const {
      formattedV3Data: formattedV3DataLastPeriod,
      V3DataCalculated: DataLastPriod
    } = getFormattedAndCalculatedV3Data(
      mappedV3DataLastPeriod,
      tableMetricsNames,
      availableMetricsWidgets,
      metricsPerGranularityMappedLastPeriod,
      2
    );

    const { trendDirection, percentChange } =
      getTrendDirectionAndPercentChangePerMetric(
        DataCurrentPriod,
        DataLastPriod,
        tableMetricsNames,
        metricsWidgetsByName
      );

    const childrenRows = [
      ...getTeamsTableRows({
        teams: teamChildren,
        metrics,
        availableMetricsWidgets,
        tableMetricsNames,
        metricsPerGranularity,
        aggregatedLastPeriodMetrics,
        metricsPerGranularityLastPeriod,
        lastPeriodDates,
        contributorsById,
        onLoadMoreClick,
        onExpandClick,
        groupsManagedPages,
        isRowLoading,
        expandedTeamId
      })
    ];

    const pageExists = !!groupsManagedPages.managedPages[id];
    const childrenInViewLength =
      groupsManagedPages.managedPages[id] * groupsManagedPages.pageSize;

    if (pageExists) {
      if (childrenInViewLength < teamChildren.length) {
        childrenRows.push({
          rowType: ROW_TYPES.LOAD_MORE,
          childrenInView: childrenInViewLength,
          totalChildren: teamChildren.length,
          onClick: () => onLoadMoreClick(team)
        });
      }
    }

    return {
      ...team,
      isRowLoading: isRowLoading && team.id === expandedTeamId,
      [TABLE_MODEL.TEAM_NAME]: <TeamNameCell team={team} />,
      children: childrenRows,
      teamHasNoData,
      ...(isGroup
        ? {
            onExpandClick
          }
        : {}),
      [TABLE_MODEL.MEMBERS]: (
        <MembersCell
          contributors={contributors}
          contributorsById={contributorsById}
        />
      ),
      [TABLE_MODEL.CYCLE_TIME]: isHovered => (
        <CycleTimeCell
          value={mappedV3Data[metricNames.CYCLE_TIME]}
          valueFormatted={formattedV3Data[metricNames.CYCLE_TIME]}
          previuosValue={DataLastPriod[metricNames.CYCLE_TIME]}
          previuosValueFormatted={
            formattedV3DataLastPeriod[metricNames.CYCLE_TIME]
          }
          mappedV3Data={mappedV3Data}
          selectedTeamId={id}
          isHovered={isHovered}
          trendIconName={getIconName(trendDirection[metricNames.CYCLE_TIME])}
          trendIconColor={_.get(
            METRICS_COLOR_PER_DIRECTION[metricNames.CYCLE_TIME],
            `${trendDirection[metricNames.CYCLE_TIME]}.color`,
            'catskillWhite'
          )}
          tooltipTitle={_.get(
            metricsWidgetsByName[metricNames.CYCLE_TIME],
            'name',
            ''
          )}
          trendDirection={trendDirection[metricNames.CYCLE_TIME]}
          percentChange={percentChange[metricNames.CYCLE_TIME]}
          lastPeriodDates={lastPeriodDates}
        />
      ),
      ...dashboardMetrics
        .filter(metricName => metricName !== metricNames.CYCLE_TIME)
        .reduce((acc, metricName) => {
          acc[metricName] = isHovered => (
            <TableCell
              value={formattedV3Data[metricName]}
              isHovered={isHovered}
              previuosValue={formattedV3DataLastPeriod[metricName]}
              trendIconName={getIconName(trendDirection[metricName])}
              trendIconColor={_.get(
                METRICS_COLOR_PER_DIRECTION[metricName],
                `${trendDirection[metricName]}.color`,
                'catskillWhite'
              )}
              trendDirection={trendDirection[metricName]}
              tooltipTitle={_.get(metricsWidgetsByName[metricName], 'name', '')}
              percentChange={percentChange[metricName]}
              lastPeriodDates={lastPeriodDates}
            />
          );
          return acc;
        }, {})
    };
  });
};
